import { memo, useCallback, useEffect, useRef, useState } from "react";

import { useParams } from "react-router-dom";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ReactComponent as AISuggestIcon } from "@/assets/ai-suggest.svg";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import AIMessage from "./AIMessage";
import UserMessage from "./UserMessage";
import DateSeparator from "./DateSeparator";
import { customScrollbarStyle } from "./style";
import { getSuggestedQuestions } from "../apis";
import ReminderContent from "./ReminderContent";
import useChatStore from "../stores/useChatStore";
import SuggestedQuestion from "./SuggestedQuestion";

let isAutoScrolling = false;

const exampleQuestions = [{ title: "Trợ lý AI có thể làm những gì?" }, { title: "Các cách để tăng doanh số?" }, { title: "MSL là gì?" }];

function ChatBox({ className, isPopupContent = false, isMobile = false }) {
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const firstInitRef = useRef({ conversationId: null, isFirstInit: true });
  const [suggestedQuestions, setSuggestedQuestions] = useState(exampleQuestions);
  const [isLoadingSuggestedQuestions, setIsLoadingSuggestedQuestions] = useState(true);
  const { chatId } = useParams();

  const setQuestion = useChatStore((store) => store.setQuestion);
  const conversation = useChatStore((store) => store.conversation);
  const reminderId = useChatStore((store) => store.reminderId);
  const conversationId = useChatStore((store) => store.conversationId);
  const handleAskQuestionStream = useChatStore((store) => store.handleAskQuestionStream);
  const isFetchingConversation = useChatStore((store) => store.isFetchingConversation);

  const scrollToBottom = useCallback((delayTime = 0) => {
    //Using setTimeout trick to delay the scroll for nexTick
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), delayTime);
  }, []);

  const onTextType = () => {
    if (isAutoScrolling) {
      scrollToBottom();
    }
  };

  const onQuestionClick = (question) => {
    scrollToBottom();
    setQuestion(question);
    handleAskQuestionStream(); // Use streaming instead of regular API
    setQuestion("");
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const SkeletonButton = ({ index = 0 }) => {
    return (
      <div className={cn("h-auto py-1.5 px-2 ml-1 rounded-lg border border-gray-200 bg-gray-50 shadow-sm", "opacity-0 animate-fadeIn animate-pulse")} style={{ animationDelay: `${index * 200}ms` }}>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-gray-300 rounded"></div>
          <div className="flex-1">
            <div className="h-3 bg-gray-300 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  };

  const getRatingFeedback = useCallback((rating) => {
    if (!rating) return null;
    return rating === 1 ? "positive" : "negative";
  }, []);

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
  };

  const groupMessagesByDate = (messages) => {
    const grouped = [];
    messages.forEach((message, index) => {
      const prevMessage = messages[index - 1];
      const showDateSeparator = !prevMessage || !isSameDay(message.createTime, prevMessage.createTime);

      if (showDateSeparator && message.createTime) {
        grouped.push({ type: "date-separator", timestamp: message.createTime });
      }

      grouped.push({ type: "message", ...message, index });
    });
    return grouped;
  };

  useEffect(() => {
    const fetchSuggestedQuestions = async () => {
      setIsLoadingSuggestedQuestions(true);
      try {
        const data = await getSuggestedQuestions();
        if (data && Array.isArray(data) && data.length > 0) {
          const mappedQuestions = data.map((item) => {
            if (typeof item === "object" && item.title) {
              return { title: item.title };
            }
            if (typeof item === "string") {
              return { title: item };
            }
            return { title: String(item) };
          });
          setSuggestedQuestions(mappedQuestions);
        }
      } catch (error) {
        console.log("Failed to fetch suggested questions, using default examples:", error);
      } finally {
        setIsLoadingSuggestedQuestions(false);
      }
    };

    if (!chatId) {
      fetchSuggestedQuestions();
    }
  }, [chatId]);

  useEffect(() => {
    // Khi chuyển conversation hoặc lần đầu tiên
    if (conversationId !== firstInitRef.current.conversationId) {
      firstInitRef.current = { conversationId: conversationId, isFirstInit: true };
      isAutoScrolling = true;
      // Scroll instant ngay lập tức khi chuyển conversation
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      }, 0);
      return;
    }

    isAutoScrolling = true;
    if (firstInitRef.current.isFirstInit) {
      // Lần đầu tiên load data của conversation hiện tại
      firstInitRef.current = { conversationId: conversationId, isFirstInit: false };
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    } else {
      // Có messages mới trong conversation hiện tại - dùng smooth scroll
      scrollToBottom(100);
    }
  }, [conversation.length, conversationId, scrollToBottom]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current.parentElement;
    if (!chatContainer) return;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const isScrolledToBottom = scrollHeight - scrollTop <= clientHeight + 12.5;

      if (isScrolledToBottom) {
        isAutoScrolling = true;
      } else {
        isAutoScrolling = false;
      }

      const isScrollable = chatContainer.scrollHeight > chatContainer.clientHeight;
      if (!isScrollable) {
        e.preventDefault();
        return;
      }
    };

    //Chặn scroll trang ngoài khi content không đủ để scroll
    !isMobile && chatContainer.addEventListener("wheel", handleWheel);
    isMobile && chatContainer.addEventListener("touchmove", handleWheel);

    return () => {
      chatContainer.removeEventListener("wheel", handleWheel);
    };
  }, [isMobile]);

  if (isFetchingConversation) {
    return <div ref={chatContainerRef} className={cn("flex-grow h-full mx-auto pt-4 px-4", className)}></div>;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div ref={chatContainerRef} className={cn("flex-grow h-full mx-auto pt-4 px-4", className)}>
        <style>{customScrollbarStyle}</style>
        {conversation.length === 0 && !reminderId && (
          <>
            <h1 className={cn("font-bold text-center mt-4 mb-4", isPopupContent ? "text-xl" : "text-3xl")}>Trợ lý AI xin chào!</h1>
            <p className={cn("text-muted-foreground mb-4 text-center p-2 rounded-sm", isPopupContent ? "text-xs" : "text-md mt-8")}>
              Tôi là trợ lý ảo của bạn, tôi sẽ giúp bạn giải đáp các thắc mắc trong kinh doanh và hướng dẫn sử dụng phần mềm.
            </p>

            <div className="mb-8 mt-6">
              <div className={cn(isPopupContent ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 gap-3 justify-center mt-8")}>
                {isLoadingSuggestedQuestions
                  ? Array.from({ length: 3 }).map((_, index) => <SkeletonButton key={`skeleton-${index}`} index={index} />)
                  : suggestedQuestions.map((q, index) => (
                      <Tooltip key={q.title}>
                        <TooltipTrigger asChild>
                          <Button
                            key={index}
                            variant="outline"
                            className={cn(
                              "h-auto py-2 px-3 ml-1 justify-start text-left flex items-center gap-2",
                              "rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/60 to-white shadow-sm",
                              "text-sm font-medium text-slate-800",
                              "hover:bg-blue-100 focus-visible:ring-2 focus-visible:ring-blue-300",
                              "animate-bound-in-up"
                            )}
                            style={{ animationDelay: `${index * 50}ms` }}
                            onClick={() => onQuestionClick(q.title)}
                          >
                            <AISuggestIcon className="w-4 h-4" />
                            {q.title}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-black text-white border-gray-700 max-w-sm p-3 text-sm" side="top" align="center">
                          <p className="whitespace-normal">{q.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
              </div>
            </div>
          </>
        )}

        <ReminderContent />

        {groupMessagesByDate(conversation).map((item, index) => {
          if (item.type === "date-separator") {
            return <DateSeparator key={`date-${index}`} date={item.timestamp} />;
          }

          if (item.role === "user") {
            return <UserMessage key={`message-${item.index}`} message={item} isPopupContent={isPopupContent} />;
          }
          if (item.role === "ai" || item.role === "assistant") {
            return (
              <AIMessage key={`message-${item.index}`} message={item} isPopupContent={isPopupContent} getRatingFeedback={getRatingFeedback} scrollToBottom={scrollToBottom} onTextType={onTextType} />
            );
          }
          return null;
        })}

        <SuggestedQuestion scrollToBottom={scrollToBottom} onQuestionClick={onQuestionClick} isPopupContent={isPopupContent} />

        <div className="h-2" />
        <div ref={messagesEndRef} />
      </div>
    </TooltipProvider>
  );
}

export default memo(ChatBox);
